import React from 'react';
import { outlierRuleText } from '../../utils/lionAutoOutliers';
import { zoneFallbackSummary } from '../../utils/lionAutoListings';

// Left-rail legend for Lion Auto: what the map colors mean, how map clicks
// behave, and a plain-words reading of the group on screen — its sections
// (naming any zone it holds whole), the filters the server actually
// applied (resolved) and its zone fallback (zoneFallbackSummary).

const MAX_ZONE_NAMES = 4;
const MAX_ROW_NAMES = 12;

// "Only A, B, C (yours and the market)" from resolved.rows ({ include }): the
// rows the server matched on; none = every row. A long pick names the first
// few and counts the rest.
export function rowsSummary(rows) {
  const include = Array.isArray(rows?.include) ? rows.include : [];
  if (include.length === 0) return 'Any row';
  const shown = include.slice(0, MAX_ROW_NAMES).join(', ');
  const more = include.length > MAX_ROW_NAMES ? ` + ${include.length - MAX_ROW_NAMES} more` : '';
  return `Only ${shown}${more} (yours and the market)`;
}

// "14 sections · all of Floor, Lower 100s". sections: [{ id }] or
// [{ sectionId }]; zones: chartGroupsFrom's list (each zone's chart section
// ids, which are Skybox section ids).
export function sectionsSummary(sections = [], zones = []) {
  const n = sections.length;
  if (!n) return 'None yet';
  const count = `${n} section${n === 1 ? '' : 's'}`;
  const ids = new Set(sections.map(s => String(s.sectionId ?? s.id)));
  const whole = zones
    .filter(z => z.sections.length > 0 && z.sections.every(s => ids.has(String(s.id))))
    .map(z => z.name);
  if (!whole.length) return count;
  const shown = whole.slice(0, MAX_ZONE_NAMES).join(', ');
  const more = whole.length > MAX_ZONE_NAMES ? ` + ${whole.length - MAX_ZONE_NAMES} more` : '';
  return `${count} · all of ${shown}${more}`;
}

// The Qty filter in words from its (minQuantity, maxQuantity) pair, 0 = no
// bound: "Any", "2+", "1 only", "2-4", "Up to 3" (the server's QTY detail
// words it the same). A max under the min is no max (the server never keeps
// that pair). The editor's Qty options read the same.
export function qtyText(minQuantity, maxQuantity) {
  const minN = Number(minQuantity);
  const maxN = Number(maxQuantity);
  const min = Number.isInteger(minN) && minN > 0 ? minN : 0;
  const max = Number.isInteger(maxN) && maxN > 0 && maxN >= min ? maxN : 0;
  if (max && min === max) return `${max} only`;
  if (max && min) return `${min}-${max}`;
  if (max) return `Up to ${max}`;
  return min ? `${min}+` : 'Any';
}

// The legend's line, from resolved.minQuantity / resolved.maxQuantity (an
// older server sends no max: no max). compsAnyQty (resolved, "1 only"): the
// filter picked your listings only and comps of any size were compared
export function qtySummary(minQuantity, maxQuantity, compsAnyQty = false) {
  const text = qtyText(minQuantity, maxQuantity);
  if (text === 'Any') return text;
  return compsAnyQty ? `${text} (yours; comps of any size)` : `${text} (yours and the market)`;
}

export function splitsSummary(vs) {
  if (!vs || vs.source === 'any') return 'Any split';
  if (!Array.isArray(vs.splits) || vs.splits.length === 0) {
    return vs.source === 'ladder' ? 'Any split: no listing of yours here to take a ladder from' : 'Any split';
  }
  return `${vs.splits.join(', ')}${vs.includeMax ? ' + full quantity' : ''} (${vs.source === 'custom' ? 'custom' : "ladder from your cheapest listing's quantity"})`;
}

// "Contain one of: VIP, parking" from resolved.notes ({ mode, terms }); a
// mode with no terms filters nothing
export function notesSummary(notes) {
  const terms = Array.isArray(notes?.terms) ? notes.terms : [];
  if (!notes || notes.mode === 'any' || terms.length === 0) return 'Any';
  return `${notes.mode === 'include' ? 'Contain one of' : 'Contain none of'}: ${terms.join(', ')} (yours and the market)`;
}

// "Ignore comps more than 25% under the 3rd cheapest · 1 dropped"
// from resolved.outlier ({ method, amount, threshold, dropped }); Off when none
export function outlierSummary(o) {
  const rule = outlierRuleText(o?.method, o?.amount, o?.threshold);
  if (!rule) return 'Off';
  return `${rule} · ${o.dropped ?? 0} dropped`;
}

// sections: what the map shows in gold (the draft's, else the selection);
// result: the group on screen's match, null when the selection is no group
function LaCriteriaLegend({ result, sections = [], zones = [], editing }) {
  const resolved = result?.resolved || null;
  return (
    <div className="group-selector-panel la-legend">
      <div className="group-selector-header">
        <span>How the group matches</span>
      </div>
      <div className="la-legend-body">
        <div className="la-legend-keys">
          <span><i className="la-swatch la-swatch-gold" /> {editing ? 'This group' : 'Selected'}</span>
          <span><i className="la-swatch la-swatch-other" /> {editing ? 'Other groups' : 'In a group'}</span>
          <span><i className="la-swatch la-swatch-hover" /> Hovered group</span>
          <span><i className="la-swatch la-swatch-empty" /> No listings</span>
        </div>
        <div className="la-hint">
          {editing
            ? 'Click a section or a zone to add or remove it. A section can be in several groups, e.g. split by the Rows or Public notes filter. Click your listings in the table to rank them for the price ladder.'
            : 'Click sections or a zone to filter the table, then Save as group to keep them. Click a group to select its sections. A section can be in several groups.'}
        </div>
        {sections.length > 0 || resolved ? (
          <dl className="la-summary">
            <dt>Sections</dt><dd>{sectionsSummary(sections, zones)}</dd>
            {resolved && (
              <>
                <dt>Qty</dt><dd>{qtySummary(resolved.minQuantity, resolved.maxQuantity, resolved.compsAnyQty)}</dd>
                <dt>Rows</dt><dd>{rowsSummary(resolved.rows)}</dd>
                <dt>Splits</dt><dd>{splitsSummary(resolved.validSplits)}</dd>
                <dt>Public notes</dt><dd>{notesSummary(resolved.notes)}</dd>
                <dt>Outliers</dt><dd>{outlierSummary(resolved.outlier)}</dd>
                <dt>Zone fallback</dt><dd>{zoneFallbackSummary(resolved)}</dd>
              </>
            )}
          </dl>
        ) : (
          <div className="la-muted">Pick or build a group to see its criteria.</div>
        )}
      </div>
    </div>
  );
}

export default LaCriteriaLegend;
